import { Box, Text } from 'ink';
import { useState, type JSX } from 'react';
import ErrorDisplay from '../../../components/ErrorDisplay';
import LoadingText from '../../../components/LoadingText';
import logger from '../../../utilities/logger';
import useExit from '../../../utilities/useExit';
import useInput from '../../../utilities/useInput';

export default function Cancellation({
  children,
  disabled = false,
  exitWhenCanceled = true,
  onCancel,
  renderCanceling = () => <LoadingText text={`Canceling ${subject}`} />,
  renderCanceled = () => <Text>{upperCaseFirstChar(subject)} canceled.</Text>,
  renderCancelText = () => (
    <Text color="gray">
      <Text bold>[esc]:</Text> cancel {subject}
    </Text>
  ),
  renderError = (e: Error) => <ErrorDisplay error={e} />,
  subject,
}: {
  children?: JSX.Element;
  disabled?: boolean;
  exitWhenCanceled?: boolean;
  onCancel: () => Promise<any>;
  renderCanceled?: () => JSX.Element;
  renderCanceling?: () => JSX.Element;
  renderCancelText?: () => JSX.Element;
  renderError?: (e: Error) => JSX.Element;
  subject: string;
}) {
  const [state, setState] = useState<CancellationState>('normal');
  const [error, setError] = useState<Error>();

  const exit = useExit();

  useInput(async (_input, key) => {
    if (key.escape) {
      cancel();
    }
  });

  function cancel() {
    if (disabled) {
      return;
    }

    logger.debug(`esc pressed, cancelling ${subject}`);
    setState('cancelling');

    onCancel()
      .then(() => {
        setState('cancelled');
        if (exitWhenCanceled) {
          setTimeout(() => exit(), 10);
        }
      })
      .catch((e) => {
        setError(e);
        setState('error');
      });
  }

  switch (state) {
    case 'cancelled':
      return renderCanceled();
    case 'cancelling':
      return renderCanceling();
    case 'error':
      return renderError(error ?? new Error(`Failed to cancel ${subject}`));
    default:
      return (
        <>
          {children}
          {!disabled && <Box>{renderCancelText()}</Box>}
        </>
      );
  }
}

type CancellationState = 'normal' | 'cancelling' | 'cancelled' | 'error';

function upperCaseFirstChar(str: string): string {
  return str[0].toUpperCase() + str.slice(1);
}
